/* eslint-disable no-unused-vars */
import { generatorAPIS } from '@/utils/generateAPIs'

/**
 * '[POST|GET|PUT|DELETE|...] [url] [d(data)(.(f|formData)):|q(query):|path:][keys with `,`]'
 *
 * d|data => data for POST and PUT
 *    - if data in request is array, you can use `[(d|data)]`;
 *    - if you want to pass all params to backend, you can use `(d|data):*`;
 *    - if you want to pass FormData to backend, you can use `(d|data).(f|formData):`;
 *
 * q|query => query for GET and DELETE;
 *
 * path => dynamic parameters in url, like: vehicle/tips/vehicleBaseInfo/{vin};
 *
 * eg.:
 *
 * import APIS from '@/api/XXX';
 *
 * APIS.testRequestUrlMethod(majorParams: Record<string, any>, otherParams?: Record<string, any>)
 *
 * If `majorParams` is array, and at the same time, you have to pass other params, you should use second param `otherParams`.
 *
 * POST:
 *    - `POST tipscase/save d:*`;
 *        equal: (params: unknown) => api.post<RoleListItem>({ url: baseUrl + 'tipscase/save', params }, true)
 *
 *    - `POST static-files d:sourceType,systemType,fileName,file,remark`;
 *        equal: (types: string[]) => api.post<Record<string, DictionaryItem[]>>({ url: baseUrl + 'static-files', params: types })
 *
 *    - `POST tipscase/save q:a,b,c`; => POST case-dict/search-types?a=[value of otherParams[a]]
 *        equal: (params: unknown) => api.post({ url: baseUrl + 'tipscase/save', params })
 *
 *    - `POST case-dict/search-types [d] q:a` => POST case-dict/search-types?a=[value of otherParams[a]] and taking majorParams as data
 *        equal: (types: string[]) => api.post<Record<string, DictionaryItem[]>>({ url: baseUrl + 'case-dict/search-types' + '?=languageType=CN', params: types })
 *
 *    ! What final `data` is depends on the keys of `d:[keys]`
 *
 * GET:
 *    - `GET tipscase/getInitInfo q:symptomId` => GET tipscase/getInitInfo?symptomId=[value of majorParams[symptomId]]
 *        equal: (symptomId: string) => api.get({ url: baseUrl + 'tipscase/getInitInfo' + symptomId })
 *
 *    - `GET tipscase/get/{id} path:id` => GET tipscase/get/[value of majorParams[id]]
 *        equal: (id: string) => api.get({ url: baseUrl + 'tipscase/get/' + id })
 * */

enum apis {
  // 上传文件
  uploadFile = 'POST static-files d.f:*',
  // 下载文件
  getDownFile = 'GET static-files/filePath q:filePath,type,fileName',
  // 修改文件名
  editFilename = 'GET static-files/edit/filename q:fileName,id,ticketId',
  // 根据类型级联查询字典信息
  getDataDictionaryByType = 'POST case-dict/search-types [data]',
  // tpr删除附件
  delSingFile = 'POST static-files/attachment/del?id={id} path:id',

  delFile = 'POST static-files/xsf-case/attachment/del d:*',

  // 获取图片base64
  getAttachmentBase64 = 'POST static-files/attachment/get/base64?filePath={filePath} path:filePath',

  // get Notices List
  getNotices = 'GET xsf-case/email/notices',
  // Exit Login
  exitLogin = 'GET openid/logout',

  // 更新附件查看状态
  updateAttachmentStatus = 'POST static-files/attachment/check/status/update [d]',
  // 更新附件审核状态
  approvedAttachment = 'POST static-files/attachment/process/status/update [d] q:isPass',
  // updateAttachmentApproved = 'POST static-files/attachment/process/status/update [d]',
  // get attachment list
  getAttachmentList = 'POST static-files/attachment/search d:*',

  // dealerInfo补偿接口
  syncDealerInfo = 'GET tipscase/compensation d:*',
  // 手动同步案例附件
  syncCaseFile = 'GET  communication/manual/case-file d:*',
  // 手动同步案例信息
  syncCaseInfo = 'GET  communication/manual/case-info d:*',
  // 手动同步HQ
  syncCaseMessage = 'GET  communication/manual/case-message d:*',

  // ==========================================================
  // 根文件夹
  fileSearch = 'GET api/shieldBff/file/search d:*', // 部门根文件夹
  fileAllSearch = 'GET api/shieldBff/menu-permission/find-all d:*', // 角色所有权限
  departmentDropDownList = 'GET api/shieldBff/department/drop-down-list d:*', // 部门下拉列表--单层
  departmentMoreDropDownList = 'GET api/shieldBff/department/departmentAttributes/drop-down-list d:*', // 部门下拉列表--多层
  departmentByRoleList = 'GET api/shieldBff/role/by-department?departmentIds={id} path:id', // 根据部门查询角色下拉列表
  departmentRoleGroupList = 'GET api/shieldBff/role/department-group d*', // 部门角色分组下拉列表
  departmentAttributesList = 'GET api/shieldBff/departmentAttributes/search d:*', // 部门属性列表

  // 部门接口
  departmentListSearch = 'GET api/shieldBff/department/search d:*',
  departmentAdd = 'POST api/shieldBff/department d:*',
  departmentPut = 'PUT api/shieldBff/department d:*',
  departmentDelete = 'DELETE api/shieldBff/department?departmentId={id} path:id',

  // 角色接口
  roleListSearch = 'POST api/shieldBff/role/search-role-info d:*',
  roleAdd = 'POST api/shieldBff/role/add-role d:*',
  rolePut = 'PUT api/shieldBff/role/put-role d:*',
  roleDelete = 'DELETE api/shieldBff/role/delete-role?roleId={id} path:id',
  roleDetail = 'GET api/shieldBff/role/id?roleId={id} path:id',

  // 用户
  getUserInfo = 'GET api/shieldBff/users/current-user',
  userListSearch = 'POST api/shieldBff/users/page d:*',
  userListExport = 'POST api/shieldBff/users/export d:*',
  usersPut = 'PUT api/shieldBff/users d:*',
  getActiveMarquee = 'GET api/users/marquee/active',

  // 文件夹管理
  getFileList = 'GET api/shieldBff/file/file-tree  d:*', // 根据部门查文件夹列表
  getRoleUserList = 'GET api/shieldBff/users/user-role-list d:*', // 根据角色查用户列表
  getRoleMenuList = 'GET api/shieldBff/menu-permission/role-menu-list d:*', // 根据角色id查询权限设置列表
  getRoleMenuPermission = 'GET api/shieldBff/menu-permission/role-menuPermission d:*', // 根据角色id查询权限回显列表
  getFindMenuPermissionCode = 'GET api/shieldBff/menu-permission/find-menuPermissionCode d:*', // 根据菜单权限code查询权限设置列表
  getFilePermissionRole = 'GET api/shieldBff/file/file-permission-role d:*', // 查询该角色有哪些文件夹的权限
  filePut = 'POST api/shieldBff/file/permission d:*',
  savePermissionByS3 = 'POST api/shieldBff/file/permission-s3-param d:*',

  // 日志
  logListSearch = 'POST api/shieldBff/log/condition-search d:*', // 查询列表
  logListExport = 'POST api/shieldBff/log/export d:*', // 导出
  getLogSimpleFileTree = 'GET api/shieldBff/file/log-simple-file-tree d:*', // 日志管理-文件树的查询
  getLogType = 'GET api/shieldBff/log/type d:*', // 日志管理-操作类型

  // 网盘
  getFileSearch = 'GET api/shieldBff/file/search d:*', // 我的网盘右侧列表查询（只展示一层）--不包含查询
  postConditionSearch = 'POST api/shieldBff/file/condition-search d:*', // 根据关键词搜索文件/文件夹
  getReadableFolderTree = 'GET api/shieldBff/file/readable-folder-tree d:*', // 我的网盘左侧目录树
  postHideOrShowFiles = 'POST api/shieldBff/file/hide-or-show-files d:*', // 显示/隐藏文件
  deleteFileDelete = 'DELETE api/shieldBff/file d:*', // 删除和批量删除文件夹/文件
  postVerifyUserExists = 'POST api/shieldBff/users/verify-user-exists [d]:*', // 校验用户是否存在
  postSharingLink = 'POST api/shieldBff/file/sharing-link d:*', // 生成分享链接
  getShareFileTree = 'GET api/shieldBff/file/share-file-tree d:*', // 根据分享id获取文件树
  postAddFolder = 'POST api/shieldBff/file/add-folder d:*', // 创建文件夹
  putUpdateFolder = 'PUT api/shieldBff/file/update-folder d:*', // 修改文件夹
  postMove = 'POST api/shieldBff/file/move d:*', // 文件/文件夹移动
  postCopy = 'POST api/shieldBff/file/copy d:*', // 文件/文件夹复制
  getWatermarkLinkd = 'GET api/shieldBff/file/watermark-link d:*', // 预览打水印

  getFileDownloadAws = 'GET api/shieldBff/file/file-download d:*', // 下载文件前获取Cognito的token
  getFileUploadAws = 'GET api/shieldBff/file/upload-preparation d:*', // 上传文件前获取Cognito的token
  postUploadInfo = 'POST api/shieldBff/file/upload-info d:*', // 保存上传文件的信息

  // 登陆后选择部门角色
  getDepartmentRole = 'GET api/shieldBff/department/department-role d:*',

  // Ddashboard
  getUserOnline = 'GET api/shieldBff/users/user-online d:*', // 昨日用户在线数
  getOpen = 'GET api/shieldBff/users/open d:*', // 启用用户数
  getCountFolders = 'GET api/shieldBff/file/count-folders d:*', // 统计文件夹数
  getCountFiles = 'GET api/shieldBff/file/count-files d:*', // 统计文件数
  getuseSpace = 'GET api/shieldBff/dashboard/use-space d:*', // 文件存储
  getNearExpiredFileStatistic = 'GET api/shieldBff/dashboard/near-expired-file-statistic d:*', // 临近文件数统计
  getdashboardSimpleFileTree = 'GET api/shieldBff/file/dashboard-simple-file-tree d:*', // dashboard-文件树的查询Name	type *0-文件树,1-目录树

  getNewFoldertatistic = 'GET api/shieldBff/dashboard/new-folder-statistic d:*', // 新增文件夹数统计--eCahrt
  getFileStatistic = 'GET api/shieldBff/dashboard/new-file-statistic d:*', // 新增文件数统计 --eCahrt
  getCuntDown = 'GET api/shieldBff/dashboard/count-download d:*', // 文件下载统计 --eCahrt
  getCuntUserOnline = 'GET api/shieldBff/users/count-user-online d:*', // 根据类型获取在线用户数量 --eCahrt
  postCountDealerUploadDownload = 'POST api/shieldBff/dashboard/count-dealer-upload-download d:*', // 经销商下载上传统计 --eCahrt

  postOpenexport = 'POST api/shieldBff/users/open/export d:*', // 用户总数-导出
  postUserOnlineExport = 'POST api/shieldBff/users/export-user-online d:*', // 昨日在线-导出
  postFilenexport = 'POST api/shieldBff/file/export d:*', // 临近过期文件-导出
  postDashboardExport = 'GET api/shieldBff/dashboard/export d:*', // dashboard-导出

  // 用户协议
  postUserFirstLogin = 'POST api/shieldBff/users/user-first-login d:*', // 记录用户首次登录
  getUserTemplate = 'GET api/shieldBff/users/template d:*', // 获取展示模板
}

export default generatorAPIS<keyof typeof apis>(apis)
